import { Dispatch, FC, SetStateAction } from 'react'
import { Box, IconButton, Link, Tooltip, Typography } from '@mui/material'
import { IconDownload, IconTrash } from '@tabler/icons-react'
import { FormikProps } from 'formik'
import { FormattedMessage } from 'react-intl'
import { Link as RouterLink } from 'react-router-dom'
import MultiImageEdit from '../../../../../components/MultiImageEdit'
import HeaderImageSelector from '../../../components/HeaderImageSelector'
import CopyLinkBtn from '../../../ReadEditOrder/components/CopyLinkButton'
import {
  allowedImageFileTypes,
  allowedPreviewFileTypes,
  allowedVideoFileTypes,
} from '../../../../../constants'
import { IdataSourceItem, IfileItem } from '../../../../../types/other'
import { downloadFilesAsZip } from '../../../../../utils/tools'
import { StyledCard, StyledCardContent, StyledCardHeader } from './styledComponents'

interface PreviewsSectionProps {
  orderId: string
  previewFiles: IfileItem[]
  setPreviewFiles: Dispatch<SetStateAction<IfileItem[]>>
  previewsDataSources: IdataSourceItem[]
  setPreviewsDataSources: Dispatch<SetStateAction<IdataSourceItem[]>>
  previewIdsToDelete: string[]
  setPreviewIdsToDelete: Dispatch<SetStateAction<string[]>>
  previewIdsToCreate: string[]
  setPreviewIdsToCreate: Dispatch<SetStateAction<string[]>>
  formik: FormikProps<any>
  permission: (field: string) => boolean
  isCustomer: boolean | string | undefined
}

export const PreviewsSection: FC<PreviewsSectionProps> = ({
  orderId,
  setPreviewFiles,
  previewsDataSources,
  setPreviewsDataSources,
  previewIdsToDelete,
  setPreviewIdsToDelete,
  previewIdsToCreate,
  setPreviewIdsToCreate,
  formik,
  permission,
  isCustomer,
}) => {
  if (!permission('previews') || isCustomer) return null

  const handleDeleteAllPreviews = () => {
    const idsToDelete = previewsDataSources.map((item) => item.id)
    setPreviewIdsToDelete(idsToDelete)
    setPreviewsDataSources([])
    setPreviewFiles([])
    formik.setFieldValue('previews', null)
    formik.setFieldValue('previewsAudio', null)
    formik.setFieldValue('previewsVideo', null)
  }

  return (
    <StyledCard>
      <StyledCardHeader
        title={<FormattedMessage id="preview" />}
        action={
          previewsDataSources.length ? (
            <Box display="flex" alignItems="center" gap="4px">
              <Link component={RouterLink} target="_blank" to={`/order-previews?id=${orderId}`}>
                <FormattedMessage id="preview-all-link" />
              </Link>
              <CopyLinkBtn str={`/order-previews?id=${orderId}`} />
              <Tooltip title="Download all">
                <IconButton
                  onClick={() => downloadFilesAsZip(previewsDataSources)}
                  className="iconOpenImage"
                >
                  <IconDownload color="blue" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete all">
                <IconButton onClick={handleDeleteAllPreviews} className="iconOpenImage">
                  <IconTrash color="red" />
                </IconButton>
              </Tooltip>
            </Box>
          ) : null
        }
      />
      <StyledCardContent>
        <MultiImageEdit
          fileAmountLimit={10}
          setFiles={setPreviewFiles}
          setDataSources={setPreviewsDataSources}
          title="Previews"
          dataSources={previewsDataSources}
          idsToDelete={previewIdsToDelete}
          setIdsToDelete={setPreviewIdsToDelete}
          idsToCreate={previewIdsToCreate}
          setIdsToCreate={setPreviewIdsToCreate}
          hasPreview
          allowedTypes={allowedPreviewFileTypes.file}
        />
      </StyledCardContent>
    </StyledCard>
  )
}

interface ImageAssetsSectionProps {
  assetsFiles: IfileItem[]
  setAssetsFiles: Dispatch<SetStateAction<IfileItem[]>>
  assetsDataSources: IdataSourceItem[]
  setAssetsDataSources: Dispatch<SetStateAction<IdataSourceItem[]>>
  assetsIdsToDelete: string[]
  setAssetsIdsToDelete: Dispatch<SetStateAction<string[]>>
  assetsIdsToCreate: string[]
  setAssetsIdsToCreate: Dispatch<SetStateAction<string[]>>
  headerImageId: string | null
  handleToggleHeaderImage: (id: string) => void
  permission: (field: string) => boolean
}

export const ImageAssetsSection: FC<ImageAssetsSectionProps> = ({
  setAssetsFiles,
  assetsDataSources,
  setAssetsDataSources,
  assetsIdsToDelete,
  setAssetsIdsToDelete,
  assetsIdsToCreate,
  setAssetsIdsToCreate,
  headerImageId,
  handleToggleHeaderImage,
  permission,
}) => {
  if (!permission('imageAssets')) return null

  return (
    <StyledCard>
      <StyledCardHeader title={<FormattedMessage id="image-assets" />} />
      <StyledCardContent>
        <MultiImageEdit
          title="Image assets"
          fileAmountLimit={5}
          setFiles={setAssetsFiles}
          setDataSources={setAssetsDataSources}
          dataSources={assetsDataSources}
          idsToCreate={assetsIdsToCreate}
          idsToDelete={assetsIdsToDelete}
          setIdsToDelete={setAssetsIdsToDelete}
          setIdsToCreate={setAssetsIdsToCreate}
          hasPreview
          allowedTypes={allowedImageFileTypes.image}
        />
        {assetsDataSources.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              <FormattedMessage id="select-header-image" />
            </Typography>
            <Box>
              {assetsDataSources
                .filter((ds) => !ds.markedToDelete)
                .map((dataSource) => (
                  <HeaderImageSelector
                    key={dataSource.id}
                    imageAssetId={dataSource.id}
                    imageAssetName={dataSource.name || 'Image'}
                    imageAssetUrl={dataSource.source}
                    isHeader={headerImageId === dataSource.id}
                    onToggleHeader={handleToggleHeaderImage}
                  />
                ))}
            </Box>
          </Box>
        )}
      </StyledCardContent>
    </StyledCard>
  )
}

interface VideoAssetsSectionProps {
  videoAssets: IfileItem[]
  setVideoAssets: Dispatch<SetStateAction<IfileItem[]>>
  videoAssetsDataSources: IdataSourceItem[]
  setVideoAssetsDataSources: Dispatch<SetStateAction<IdataSourceItem[]>>
  videoAssetsIdsToDelete: string[]
  setVideoAssetsIdsToDelete: Dispatch<SetStateAction<string[]>>
  videoAssetsIdsToCreate: string[]
  setVideoAssetsIdsToCreate: Dispatch<SetStateAction<string[]>>
  permission: (field: string) => boolean
}

export const VideoAssetsSection: FC<VideoAssetsSectionProps> = ({
  setVideoAssets,
  videoAssetsDataSources,
  setVideoAssetsDataSources,
  videoAssetsIdsToDelete,
  setVideoAssetsIdsToDelete,
  videoAssetsIdsToCreate,
  setVideoAssetsIdsToCreate,
  permission,
}) => {
  if (!permission('videoAssets')) return null

  return (
    <StyledCard>
      <StyledCardHeader title={<FormattedMessage id="video-assets" />} />
      <StyledCardContent>
        <MultiImageEdit
          title="Video assets"
          fileAmountLimit={4}
          setFiles={setVideoAssets}
          setDataSources={setVideoAssetsDataSources}
          dataSources={videoAssetsDataSources}
          idsToCreate={videoAssetsIdsToCreate}
          idsToDelete={videoAssetsIdsToDelete}
          setIdsToDelete={setVideoAssetsIdsToDelete}
          setIdsToCreate={setVideoAssetsIdsToCreate}
          hasPreview={false}
          allowedTypes={allowedVideoFileTypes.video}
        />
      </StyledCardContent>
    </StyledCard>
  )
}
